import React, { useMemo, useState } from 'react';
import RevenueReforecastCard from './RevenueReforecastCard';
import BudgetReadiness from './BudgetReadiness';
import './OperatingBudgetCard.css';

const money = value => Number(value || 0).toLocaleString(undefined, {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0
});

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = key => MONTH_LABELS[Number(String(key).slice(5, 7)) - 1] || key;

/**
 * A built budget, shown as a statement rather than as a list of proposals.
 *
 * The question a reviewer opens this with is "what does this budget say?", not
 * "which drivers changed". So the totals lead, the departments follow, and the
 * accounts that could NOT be budgeted get their own section — a budget with
 * silent holes reads complete and is not.
 */
const OperatingBudgetCard = ({ artifact }) => {
  const data = artifact?.data || {};
  const statement = data.statement || null;
  const current = data.current || null;
  // Biggest gaps first. A list opening with three $600 lines reads as trivia;
  // the same list opening with a six-figure hole reads as the finding it is.
  const uncovered = useMemo(
    () => (Array.isArray(data.unbudgeted) ? data.unbudgeted : [])
      .filter(item => !item.covered)
      .sort((a, b) => (b.signal || 0) - (a.signal || 0)),
    [data.unbudgeted]
  );
  const dormant = Array.isArray(data.dormant) ? data.dormant : [];
  const reconciliation = data.reconciliation || null;
  // Low-severity month-shape notes are informational; they belong behind the
  // same disclosure as everything else but must not inflate the headline.
  const reconFailures = useMemo(
    () => (reconciliation?.checks || []).filter(check => check.severity !== 'low'),
    [reconciliation]
  );
  const [showMonths, setShowMonths] = useState(false);

  const delta = statement && current
    ? statement.operatingIncome - current.operatingIncome
    : null;

  if (!statement) {
    return (
      <section className="ob-card" aria-label="Operating budget">
        <div className="ob-title">{data.year ? `${data.year} operating budget` : 'Operating budget'}</div>
        <div className="ob-empty">
          {(data.operatingLimitations || [])[0] || 'The operating statement could not be previewed for this run.'}
        </div>
        <BudgetReadiness data={data} />
      </section>
    );
  }

  return (
    <section className="ob-card" aria-label="Operating budget">
      <div className="ob-head">
        <div className="ob-title">{data.year} operating budget</div>
        {data.historyAsOf && (
          <div className="ob-sub">Built from history through {data.historyAsOf} — no {data.year} information was used.</div>
        )}
      </div>

      <BudgetReadiness data={data} />

      <div className="ob-tiles">
        <div className="ob-tile">
          <span>Revenue</span>
          <strong>{money(statement.revenue)}</strong>
        </div>
        <div className="ob-tile">
          <span>Expense</span>
          <strong>{money(statement.expense)}</strong>
        </div>
        <div className="ob-tile is-result">
          <span>Operating income</span>
          <strong>{money(statement.operatingIncome)}</strong>
          {statement.operatingMarginPct != null && <em>{statement.operatingMarginPct}% margin</em>}
        </div>
        {delta != null && delta !== 0 && (
          <div className="ob-tile">
            <span>vs current draft</span>
            <strong className={delta >= 0 ? 'is-positive' : 'is-negative'}>
              {delta >= 0 ? '+' : ''}{money(delta)}
            </strong>
          </div>
        )}
      </div>

      {Number.isFinite(Number(data.accountsReviewed)) && (
        <div className="ob-coverage">
          {/*
            Coverage is reported against ACTIVE accounts. Measuring it against
            every row on the grid measures the budget against a chart of
            accounts, and a chart carrying hundreds of unused rows makes a
            complete budget look two-thirds finished.
          */}
          {data.accountsCovered} of {data.activeAccounts || data.accountsReviewed} active
          {' '}account{(data.activeAccounts || data.accountsReviewed) === 1 ? '' : 's'} covered
          {uncovered.length > 0 && <> · <span className="is-warn">{uncovered.length} with no budget method</span></>}
          {dormant.length > 0 && <> · {dormant.length} inactive excluded</>}
        </div>
      )}

      {statement.byDepartment?.length > 0 && (
        <div className="ob-table" role="table" aria-label="Budget by department">
          <div className="ob-row ob-head-row" role="row">
            <span role="columnheader">Department</span>
            <span role="columnheader">Revenue</span>
            <span role="columnheader">Expense</span>
            <span role="columnheader">Operating income</span>
          </div>
          {statement.byDepartment.map(entry => (
            <div className="ob-row" role="row" key={entry.department}>
              <span role="cell" className="ob-dept">{entry.department}</span>
              <span role="cell" className="ob-num">{entry.revenue ? money(entry.revenue) : '—'}</span>
              <span role="cell" className="ob-num">{entry.expense ? money(entry.expense) : '—'}</span>
              <span role="cell" className={`ob-num ${entry.operatingIncome >= 0 ? 'is-positive' : 'is-negative'}`}>
                {money(entry.operatingIncome)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/*
        The budget next to the property's own history. Every individual driver
        in a budget can be reasonable while the statement they add up to is
        half a million dollars short, and nothing in a list of proposals shows
        that — so the comparison gets its own section rather than a footnote.
      */}
      {reconFailures.length > 0 && (
        <div className={`ob-recon ${reconciliation.blocking?.length ? 'is-blocking' : ''}`}>
          <div className="ob-recon-head">
            {reconciliation.blocking?.length > 0
              ? 'These lines do not reconcile against this property’s own history — totals are withheld until they are resolved'
              : 'Lines that do not reconcile against this property’s own history'}
          </div>
          <ul className="ob-recon-list">
            {reconFailures.map((check, index) => (
              <li key={`${check.kind}-${check.department || check.chartOfAccountId || index}`}
                  className={check.severity === 'high' ? 'is-high' : ''}>
                <div>{check.summary}</div>
                {check.comparisons?.length > 0 && (
                  <div className="ob-recon-refs">
                    {check.comparisons.map(comparison => (
                      <span key={comparison.referenceKey}>
                        {comparison.reference}: {money(comparison.referenceValue)}
                        {comparison.deltaPct != null && (
                          <em className={comparison.deltaPct >= 0 ? 'is-positive' : 'is-negative'}>
                            {comparison.deltaPct >= 0 ? '+' : ''}{comparison.deltaPct}%
                          </em>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {statement.monthly?.length > 0 && (
        <details className="ob-details" open={showMonths} onToggle={event => setShowMonths(event.target.open)}>
          <summary>Month by month</summary>
          <div className="ob-table" role="table" aria-label="Budget by month">
            <div className="ob-row ob-head-row" role="row">
              <span role="columnheader">Month</span>
              <span role="columnheader">Revenue</span>
              <span role="columnheader">Expense</span>
              <span role="columnheader">Operating income</span>
            </div>
            {statement.monthly.map(month => (
              <div className="ob-row" role="row" key={month.month}>
                <span role="cell" className="ob-dept">{monthLabel(month.month)}</span>
                <span role="cell" className="ob-num">{money(month.revenue)}</span>
                <span role="cell" className="ob-num">{money(month.expense)}</span>
                <span role="cell" className={`ob-num ${month.operatingIncome >= 0 ? 'is-positive' : 'is-negative'}`}>
                  {money(month.operatingIncome)}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {uncovered.length > 0 && (
        <details className="ob-details is-warn">
          <summary>{uncovered.length} account{uncovered.length === 1 ? '' : 's'} with no budget method</summary>
          <ul className="ob-unbudgeted">
            {uncovered.map(item => (
              <li key={item.chartOfAccountId}>
                <strong>{item.account}</strong>
                {item.department && <small> · {item.department}</small>}
                {/* What is standing behind the gap, so a $296k hole is not
                    read at the same weight as a $600 one. */}
                {item.signal > 0 && <small className="is-warn"> · {money(item.signal)} of history or prior budget</small>}
                <div>{item.reason}</div>
              </li>
            ))}
          </ul>
        </details>
      )}

      {dormant.length > 0 && (
        <details className="ob-details">
          <summary>{dormant.length} inactive account{dormant.length === 1 ? '' : 's'} excluded</summary>
          <p className="ob-note">
            No actuals in the trailing year or the last complete year, and no budget in either.
            These are not budget gaps.
          </p>
          <ul className="ob-unbudgeted">
            {dormant.slice(0, 40).map(item => (
              <li key={item.chartOfAccountId}>
                <strong>{item.account}</strong>
                {item.department && <small> · {item.department}</small>}
              </li>
            ))}
          </ul>
          {dormant.length > 40 && <p className="ob-note">…and {dormant.length - 40} more.</p>}
        </details>
      )}

      {(reconciliation?.checks || []).some(check => check.severity === 'low') && (
        <details className="ob-details">
          <summary>Month-shape notes versus the same month a year earlier</summary>
          <ul className="ob-unbudgeted">
            {reconciliation.checks.filter(check => check.severity === 'low').map((check, index) => (
              <li key={index}><div>{check.summary}</div></li>
            ))}
          </ul>
        </details>
      )}

      {data.roomsStatPlan && <RevenueReforecastCard artifact={{ ...artifact, data }} />}

      {(data.operatingLimitations || []).length > 0 && (
        <details className="ob-details">
          <summary>What this budget does not claim</summary>
          <ul className="ob-unbudgeted">
            {data.operatingLimitations.map((text, index) => <li key={index}><div>{text}</div></li>)}
          </ul>
        </details>
      )}
    </section>
  );
};

export default OperatingBudgetCard;
